import {
  dlpPath,
  homePath,
  isInquiry,
  isDLP,
  isHome,
  isListing,
  isSearch,
  searchPath,
  isSearchWithExtraParams,
  isBookNow,
  listingPathFromBookNowPath,
  newURLWithoutLocale,
} from 'src/common/components/MobileWebViewHeader/backNavigation/utils';
import type { RequestInfoContext } from 'src/common/providers/RequestInfoProvider';
import captureException from 'src/common/utils/captureException';
import isDarkCode from 'src/common/utils/isDarkCode';
import type { MinimalNextRouter, BackNavigationLocation } from 'src/common/components/MobileWebViewHeader/backNavigation/types';

/**
 * if `null` omit button
 * if 'default' the button onClick should call router.back()
 * else, use the string as href in UniversalLink (for pre-fetch)
*/
type BackNavigationReturn = string | 'routerBack' | null;

type BackNavigation = (
  router: MinimalNextRouter,
  requestInfo: Pick<RequestInfoContext, 'referrer' | 'host' | 'protocol'>,
  catLocBreadcrumb?: BackNavigationLocation,
) => BackNavigationReturn;

/**
 * Handles dynamic back routing based on referrer and relative position in hierarchy
 * In homepage or fallback case, this returns null to omit back button
 * In "sibling" cases, this returns the router.back function to call in button onClick
 * In most cases, this returns an href path to use in next/link (for prefetch)
 * Usually the returned path is the referrer asPath.
 * If referrer is not gmb, the path will be 1 left in the hierarchy:
 * home > dlp > search > searchWithFilters > listing > booking-inquiry
*/
const backNavigation: BackNavigation = (
  router,
  requestInfo,
  catLocBreadcrumb,
) => {
  if (isHome(router)) return null; // null is signal to omit back button

  const gmbOrigin = `${requestInfo.protocol}//${requestInfo.host}/`;

  // From external origin, back should next/link left in hierarchy: home > dlp > search > listing
  if (!requestInfo.referrer?.startsWith(gmbOrigin)) {
    if (isListing(router)) { return searchPath(catLocBreadcrumb); }
    if (isSearch(router)) { return dlpPath(router); }
    if (isDLP(router)) { return homePath(); }
  }

  if (requestInfo.referrer) {
    try {
      const referrer = newURLWithoutLocale(requestInfo.referrer);

      if (isListing(router)) {
        if (referrer.pathname === router.asPath) {
          // Occurs when progressing through similar listings, then back to initial listing.
          return searchPath(catLocBreadcrumb);
        }
        if (isListing(referrer) || isSearch(referrer) || isDLP(referrer) || isHome(referrer)) {
          return 'routerBack';
        }
        if (isBookNow(referrer)) {
          // hierarchy back to avoid looping between listing and book-now
          return searchPath(catLocBreadcrumb); // TODO: consider appending recentSearch filters
        }
        if (isInquiry(referrer)) {
          // hierarchy back to avoid looping between listing and inquiry
          return searchPath(catLocBreadcrumb);
        }
        return searchPath(catLocBreadcrumb);
      }
      if (isBookNow(router)) {
        if (isListing(referrer)) {
          return 'routerBack';
        }
        return listingPathFromBookNowPath(router);
      }
      if (isSearch(router)) {
        if (isSearchWithExtraParams(router)) {
          // avoid stepping back through possibly many filters entries; push left to search?page=1
          return searchPath(catLocBreadcrumb);
        }
        if (isSearchWithExtraParams(referrer)) {
          // this scenario is only possible after the above block
          // for second back click, push left to DLP
          return dlpPath(router);
        }
        if (isSearch(referrer) || isDLP(referrer) || isHome(referrer)) {
          return 'routerBack';
        }
        return dlpPath(router);
      }
      if (isDLP(router)) {
        if (isDLP(referrer) || isHome(referrer)) {
          return 'routerBack';
        }
        return homePath();
      }

      if (isListing(router)) {
        return 'routerBack';
      }
    } catch (e) {
      captureException(new Error('Back Navigation Error constructing URL'));
    }
  }

  // If referrer is unknown, default push 1 left in hierarchy
  if (!requestInfo.referrer) {
    if (isBookNow(router)) {
      // prevents back loop between book-now and stripe
      return listingPathFromBookNowPath(router);
    }
    if (isListing(router)) {
      return searchPath(catLocBreadcrumb);
    }
    if (isSearch(router) || isSearchWithExtraParams(router)) {
      return dlpPath(router);
    }
    if (isDLP(router)) {
      return homePath();
    }
  }

  if (typeof window !== 'undefined' && isDarkCode()) {
    // Avoids spamming sentry prod quota
    captureException(new Error('Back Navigation Warning: Unknown case'));
  }

  return 'routerBack';
};

export default backNavigation;
